import React, { memo, useCallback, useEffect, useRef, useState } from "react";
import Webcam from "../../../../components/qrcode-reader/Webcam";
import { BarcodeFormat, BrowserMultiFormatReader, DecodeHintType } from "@zxing/library";

const hints = new Map();
hints.set(DecodeHintType.POSSIBLE_FORMATS, [BarcodeFormat.QR_CODE]);

export const ReturnQRCodeReader = memo(({ handleSuccess, handleError, height = "300px" }) => {
  const audioRef = useRef(null);
  const videoRef = useRef(null);
  const codeReader = useRef(null);
  const [loading, setLoading] = useState(true);
  const [imageSrc, setImageSrc] = useState("");

  const handleSetSuccesImage = useCallback(() => {
    const videoElement = videoRef.current ? videoRef.current : null;
    if (!videoElement) return null;
    const canvas = document.createElement("canvas");
    canvas.width = videoElement.width || 600;
    canvas.height = videoElement.height || 300;
    const context = canvas.getContext("2d");
    if (context) {
      context.drawImage(videoElement, 0, 0, canvas.width, canvas.height);
      setImageSrc(canvas.toDataURL());
    }
  }, []);

  const decodeVideo = useCallback(async (videoDeviceId) => {
    const videoConstraints = { facingMode: "environment" };
    const constraints = { video: videoDeviceId ? { deviceId: videoDeviceId } : videoConstraints };

    const videoElement = videoRef.current ? videoRef.current : null;
    if (!videoElement) return null;

    const stream = videoElement.srcObject;
    if (stream !== null) {
      stream.getTracks().forEach((track) => track.stop());
    }

    const reader = new BrowserMultiFormatReader(hints, 2000);
    codeReader.current = reader;
    let hasResult = false;
    setLoading(true);
    reader.stopAsyncDecode();

    reader
      .decodeOnceFromConstraints(constraints, videoElement)
      .then((res) => {
        if (res && !hasResult) {
          hasResult = true;
          const audioElem = audioRef.current ? audioRef.current : null;
          if (audioElem) audioElem.play();
          handleSetSuccesImage();
          handleSuccess(res.getText());
        }
      })
      .catch((error) => {
        if (
          error.name === "NotFoundError" ||
          error.name === "TypeError" ||
          error.name === "SecurityError" ||
          error.name === "OverconstrainedError" ||
          error.name === "NotSupportedError" ||
          error.name === "NotReadableError" ||
          error.name === "NotAllowedError" ||
          error.name === "AbortError" ||
          error.name === "PermissionDeniedError"
        ) {
          handleError(error);
          setLoading(false);
        } else {
          decodeVideo(videoDeviceId);
        }
      });
  }, [videoRef.current, audioRef.current, handleSuccess]);

  useEffect(() => {
    if (videoRef.current) {
      videoRef.current.onplaying = () => {
        setLoading(false);
      };
    }
  }, [videoRef]);

  useEffect(() => {
    return () => {
      if (codeReader.current) {
        codeReader.current.reset();
        codeReader.current.stopContinuousDecode();
      }
      if (videoRef.current && videoRef.current.srcObject) {
        const stream = videoRef.current.srcObject;
        stream.getTracks().forEach((track) => track.stop());
      }
    };
  }, []);

  return (
    <div>
      {imageSrc ? (
        <img src={imageSrc} alt="" style={{ width: "100%", height: height }} />
      ) : (
        <Webcam
          handleError={handleError}
          style={{ width: "100%", height: height, margin: "0 auto", backgroundColor: "transparent" }}
          id="webcam_return"
          ref={videoRef}
          loading={loading}
          disableSrcObject={true}
          handleVideoDeviceSelect={decodeVideo}
        />
      )}
      <audio ref={audioRef}>
        <source src="beep.mp3" type="audio/mpeg" />
      </audio>
    </div>
  );
});
